import { Either, Option } from "effect";
import { resourceOfEventName } from "../interpreter/await-arms.ts";
import {
  assignable,
  checkSubset,
  collectRefs,
  itemsOf,
  OUTCOME_INVALID,
  propertyAt,
  resolveRefIn,
  type SchemaResolver,
  schemaResolver,
} from "./outcome-schema.ts";
import { EXPRESSION_ROOT_HEADS, isSinkNode } from "./schema.ts";
import type { LoopDocument, LoopNode, NodeId, ValidatedDocument, ValidationFinding, ValidationRule } from "./types.ts";

// ---------------------------------------------------------------------------
// The load-time document validator (model v3.2 §2, §3, §5, §8, §10).
//
// A document that parses is not yet a document that can run. This validator
// fixes the static rules the model enforces at load — one outcome, every path
// reaching it, routes that resolve, `live:` that declares its `close:`, a
// fan-out whose `depends_on` edges form a DAG, `pool:` references that name a
// declared pool, head fields that are declared, expression roots from the
// fixed set, and `@version` references that resolve.
//
// Findings are ACCUMULATED, never short-circuited: a document with three
// problems reports three findings, each carrying its `ValidationRule`, the node
// it belongs to, and a detail string. The rule ids are a contract (Project C's
// definitions RPC returns them to the caller), so they are stable, not log
// text.
// ---------------------------------------------------------------------------

/** A `${…}` expression's leading roots, the set a leading-identifier check compares against. */
const VALID_ROOTS: ReadonlySet<string> = new Set<string>(EXPRESSION_ROOT_HEADS);

/** Free-form boolean-predicate keys whose `${…}` bodies name the occupied node, not a value root. */
const PREDICATE_KEYS: ReadonlySet<string> = new Set<string>(["when", "unless", "trust"]);

/** A duration literal: a number plus a unit (`s`, `m`, `h`, `d`, or `bd` business days; model §4). */
const DURATION = /^\d+(s|m|h|d|bd)$/;

/** The leading identifier of a `${…}` expression. */
const EXPRESSION = /\$\{\s*([A-Za-z_][A-Za-z0-9_]*)/g;

/** A direct `nodes.<name>.outcome.<field>` head-field read. */
const HEAD_READ = /nodes\.([A-Za-z_][A-Za-z0-9_]*)\.outcome\.([A-Za-z_][A-Za-z0-9_]*)/g;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Normalise a `T | ReadonlyArray<T> | undefined` schema union to an array. */
const toArray = <T>(value: T | ReadonlyArray<T> | undefined): ReadonlyArray<T> =>
  value === undefined ? [] : Array.isArray(value) ? (value as ReadonlyArray<T>) : [value as T];

/** Build a finding, omitting `node` when the violation is graph-level. */
const finding = (rule: ValidationRule, detail: string, node?: NodeId): ValidationFinding =>
  node === undefined ? { rule, detail } : { rule, node, detail };

// --- expression collection -------------------------------------------------

interface Scanned {
  readonly text: string;
  readonly predicate: boolean;
  readonly node: NodeId | undefined;
}

const collectStrings = (
  value: unknown,
  predicate: boolean,
  node: NodeId | undefined,
  out: Array<Scanned>,
): void => {
  if (typeof value === "string") {
    out.push({ text: value, predicate, node });
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      collectStrings(item, predicate, node, out);
    }
    return;
  }
  if (isRecord(value)) {
    for (const key of Object.keys(value)) {
      collectStrings(value[key], predicate || PREDICATE_KEYS.has(key), node, out);
    }
  }
};

/** Every string in the document, tagged with its enclosing node id and whether it sits in a predicate. */
const scanDocument = (document: LoopDocument): ReadonlyArray<Scanned> => {
  const out: Array<Scanned> = [];
  for (const [id, node] of Object.entries(document.nodes ?? {})) {
    collectStrings(node, false, id, out);
  }
  collectStrings(document.trigger, false, undefined, out);
  collectStrings(document.watch, false, undefined, out);
  collectStrings(document.pools, false, undefined, out);
  collectStrings(document.inputs, false, undefined, out);
  collectStrings(document.workspace, false, undefined, out);
  collectStrings(document.harness, false, undefined, out);
  return out;
};

// --- version resolution ----------------------------------------------------

/** A `name@N` / `name@latest` reference split into its parts. */
const parseRef = (ref: string): { readonly name: string; readonly spec: string } | undefined => {
  const at = ref.lastIndexOf("@");
  return at < 0 ? undefined : { name: ref.slice(0, at), spec: ref.slice(at + 1) };
};

const versionOf = (document: LoopDocument): number => document.version === undefined ? 0 : document.version;

/** The highest-versioned document in a same-name group (the `@latest` target). */
const highest = (group: ReadonlyArray<LoopDocument>): LoopDocument | undefined => {
  let best: LoopDocument | undefined = undefined;
  for (const document of group) {
    if (best === undefined || versionOf(document) > versionOf(best)) {
      best = document;
    }
  }
  return best;
};

interface Resolution {
  readonly nameKnown: boolean;
  readonly document: LoopDocument | undefined;
}

const resolveRef = (index: Map<string, Array<LoopDocument>>, ref: string): Resolution | undefined => {
  const parsed = parseRef(ref);
  if (parsed === undefined) {
    return undefined;
  }
  const group = index.get(parsed.name);
  if (group === undefined) {
    return { nameKnown: false, document: undefined };
  }
  const document = parsed.spec === "latest"
    ? highest(group)
    : group.find((candidate) => candidate.version === Number(parsed.spec));
  return { nameKnown: true, document };
};

// --- route targets ---------------------------------------------------------

type RouteValue = string | { readonly to: string; readonly delay?: unknown; readonly when?: unknown };

/** A route's destination node, or `undefined` when the route is `stay` (or object-`to` is `stay`). */
const routeTarget = (value: RouteValue): string | undefined => {
  const to = typeof value === "string" ? value : value.to;
  return to === "stay" ? undefined : to;
};

// --- fan-out live spec ------------------------------------------------------

interface LiveSpec {
  readonly close?: unknown;
  readonly on_vanish?: unknown;
  readonly on_late?: unknown;
}

// --- cycle detection (model §5) --------------------------------------------

const detectCycle = (edges: Record<string, ReadonlyArray<string>>): ReadonlyArray<string> | undefined => {
  const WHITE = 0;
  const GRAY = 1;
  const color = new Map<string, number>();
  const path: Array<string> = [];
  let cycle: ReadonlyArray<string> | undefined = undefined;
  const visit = (node: string): boolean => {
    color.set(node, GRAY);
    path.push(node);
    for (const next of edges[node] ?? []) {
      const seen = color.get(next) ?? WHITE;
      if (seen === GRAY) {
        cycle = [...path, next];
        return true;
      }
      if (seen === WHITE && visit(next)) {
        return true;
      }
    }
    color.set(node, 2);
    path.pop();
    return false;
  };
  for (const node of Object.keys(edges)) {
    if ((color.get(node) ?? WHITE) === WHITE && visit(node)) {
      break;
    }
  }
  return cycle;
};

// --- the rules -------------------------------------------------------------

const checkGraph = (
  document: LoopDocument,
  nodes: Record<string, LoopNode>,
  findings: Array<ValidationFinding>,
): void => {
  const entries = Object.entries(nodes);
  const ids = entries.map(([id]) => id);
  const declared = new Set<string>(ids);
  const pools = new Set<string>(Object.keys(document.pools ?? {}));
  const hasRoutes = (node: LoopNode): boolean => node.routes !== undefined && Object.keys(node.routes).length > 0;
  const foreachBlock = (node: LoopNode): Record<string, unknown> | undefined =>
    isRecord(node.for_each) ? node.for_each : undefined;

  // Rule 1: one_sink — at least one terminal, and a terminal declares no routes.
  const terminals = entries.filter(([, node]) => isSinkNode(node)).map(([id]) => id);
  if (terminals.length === 0) {
    findings.push(finding("one_sink", "the graph declares no terminal (outcome: true) node"));
  }
  for (const id of terminals) {
    if (nodes[id].routes !== undefined) {
      findings.push(finding("one_sink", "a terminal must not declare routes; it is the graph's verdict", id));
    }
  }

  // Forward edges: routes, timeouts, `every`, a fan-out's close-evaluator, and
  // the implicit fall-through of a routeless non-terminal loop node.
  const edges = new Map<string, Set<string>>();
  const edgeSet = (id: string): Set<string> => {
    const existing = edges.get(id);
    if (existing !== undefined) {
      return existing;
    }
    const fresh = new Set<string>();
    edges.set(id, fresh);
    return fresh;
  };
  const addEdge = (from: string, target: string | undefined): void => {
    if (target !== undefined && target !== "stay") {
      edgeSet(from).add(target);
    }
  };
  entries.forEach(([id, node], index) => {
    for (const value of Object.values(node.routes ?? {})) {
      addEdge(id, routeTarget(value));
    }
    for (const timeout of toArray(node.timeout)) {
      addEdge(id, timeout.route);
    }
    if (node.every !== undefined) {
      addEdge(id, node.every.route);
    }
    const close = (foreachBlock(node) ?? node).close;
    if (typeof close === "string" && !DURATION.test(close) && !close.includes("${") && declared.has(close)) {
      edgeSet(id).add(close);
    }
    if (!isSinkNode(node) && !hasRoutes(node) && index + 1 < ids.length) {
      edgeSet(id).add(ids[index + 1]);
    }
  });

  // Rule 2a: path_reaches_terminal — every node reachable from the entry, with
  // graph-level watch routes as additional entry points.
  const watchTargets = toArray(document.watch).flatMap((watch) =>
    typeof watch.route === "string" && watch.route !== "stay" ? [watch.route] : []
  );
  const reachable = new Set<string>();
  const queue = [ids[0], ...watchTargets.filter((target) => declared.has(target))];
  for (let i = 0; i < queue.length; i++) {
    const current = queue[i];
    if (reachable.has(current)) {
      continue;
    }
    reachable.add(current);
    for (const next of edges.get(current) ?? new Set<string>()) {
      if (declared.has(next) && !reachable.has(next)) {
        queue.push(next);
      }
    }
  }
  for (const id of ids) {
    if (!reachable.has(id)) {
      findings.push(finding("path_reaches_terminal", "node is unreachable from the entry", id));
    }
  }

  // Rule 2b: from every non-terminal node some route path reaches a terminal.
  const reverse = new Map<string, Set<string>>();
  for (const [from, set] of edges) {
    for (const to of set) {
      const bucket = reverse.get(to) ?? new Set<string>();
      bucket.add(from);
      reverse.set(to, bucket);
    }
  }
  const reachesTerminal = new Set<string>(terminals);
  const back = [...terminals];
  for (let i = 0; i < back.length; i++) {
    for (const predecessor of reverse.get(back[i]) ?? new Set<string>()) {
      if (!reachesTerminal.has(predecessor)) {
        reachesTerminal.add(predecessor);
        back.push(predecessor);
      }
    }
  }
  for (const [id, node] of entries) {
    if (!isSinkNode(node) && !reachesTerminal.has(id)) {
      findings.push(finding("path_reaches_terminal", "no route path from this node reaches a terminal", id));
    }
  }

  // Rule 3: routes_cover_selection.
  const checkTarget = (id: NodeId | undefined, key: string, value: RouteValue): void => {
    const target = routeTarget(value);
    if (target !== undefined && !declared.has(target)) {
      findings.push(finding("routes_cover_selection", `route \`${key}\` names an undeclared node \`${target}\``, id));
    }
  };
  const drawnPools = new Set<string>();
  for (const [, node] of entries) {
    for (const bound of toArray(node.bounds)) {
      if (bound.pool !== undefined) {
        drawnPools.add(bound.pool);
      }
    }
  }
  for (const [id, node] of entries) {
    const routes = node.routes ?? {};
    for (const [key, value] of Object.entries(routes)) {
      checkTarget(id, key, value);
    }
    for (const timeout of toArray(node.timeout)) {
      if (timeout.route !== undefined) {
        checkTarget(id, "timeout", timeout.route);
      }
    }
    if (node.every !== undefined && node.every.route !== undefined) {
      checkTarget(id, "every", node.every.route);
    }
    if (node.evaluator !== undefined && Object.keys(routes).length === 0) {
      findings.push(finding("routes_cover_selection", "an evaluator must declare at least one route", id));
    }
    if (
      node.max_attempts !== undefined && Object.keys(routes).length > 0 && routes["attempts_exhausted"] === undefined
    ) {
      findings.push(
        finding("routes_cover_selection", "a node with `max_attempts` needs an `attempts_exhausted` route", id),
      );
    }
    for (const key of Object.keys(routes)) {
      const match = /^(.+)_exhausted$/.exec(key);
      if (match !== null && pools.has(match[1]) && !drawnPools.has(match[1])) {
        findings.push(
          finding(
            "routes_cover_selection",
            `route \`${key}\` names pool \`${match[1]}\` that no node draws from`,
            id,
          ),
        );
      }
    }
  }
  for (const watch of toArray(document.watch)) {
    if (typeof watch.route === "string") {
      checkTarget(undefined, "watch", watch.route);
    }
  }

  // Rule 4: live_requires_close.
  for (const [id, node] of entries) {
    const block = foreachBlock(node);
    const liveObject: LiveSpec | undefined = isRecord(node.live) ? node.live : undefined;
    const isFanout = node.for_each !== undefined;
    const isLive = (block !== undefined && block["live"] === true) || node.live === true || liveObject !== undefined;
    if (isFanout && isLive) {
      const sources: ReadonlyArray<LiveSpec> = [block, liveObject, node].filter((
        source,
      ): source is LiveSpec => source !== undefined);
      const pick = (key: keyof LiveSpec): unknown => {
        for (const source of sources) {
          if (source[key] !== undefined) {
            return source[key];
          }
        }
        return undefined;
      };
      const close = pick("close");
      if (close === undefined) {
        findings.push(finding("live_requires_close", "a live fan-out must declare `close`", id));
      }
      if (pick("on_vanish") === undefined) {
        findings.push(finding("live_requires_close", "a live fan-out must declare `on_vanish`", id));
      }
      if (pick("on_late") === undefined) {
        findings.push(finding("live_requires_close", "a live fan-out must declare `on_late`", id));
      }
      if (typeof close === "string" && !DURATION.test(close) && !close.includes("${")) {
        if (!declared.has(close)) {
          findings.push(finding("live_requires_close", `\`close\` names an undeclared node \`${close}\``, id));
        } else if (nodes[close].evaluator === undefined) {
          findings.push(
            finding("live_requires_close", `\`close\` names \`${close}\`, a loop node, not an evaluator`, id),
          );
        }
      }
    }
  }

  // Rule 5: fanout_dag.
  for (const [id, node] of entries) {
    const dependsOn = node.depends_on;
    const rawEdges = dependsOn === undefined ? undefined : dependsOn.edges;
    if (isRecord(rawEdges)) {
      const adjacency: Record<string, ReadonlyArray<string>> = {};
      for (const [from, tos] of Object.entries(rawEdges)) {
        adjacency[from] = Array.isArray(tos) ? tos.filter((to): to is string => typeof to === "string") : [];
      }
      const cycle = detectCycle(adjacency);
      if (cycle !== undefined) {
        findings.push(finding("fanout_dag", `\`depends_on\` edges form a cycle: ${cycle.join(" -> ")}`, id));
      }
    }
  }

  // Rule 6: pool_declared.
  for (const [id, node] of entries) {
    for (const bound of toArray(node.bounds)) {
      if (bound.pool !== undefined && !pools.has(bound.pool)) {
        findings.push(finding("pool_declared", `bound draws from undeclared pool \`${bound.pool}\``, id));
      }
    }
  }
};

const checkExpressions = (
  document: LoopDocument,
  scanned: ReadonlyArray<Scanned>,
  findings: Array<ValidationFinding>,
): void => {
  const hasTrigger = document.trigger !== undefined;
  const seen = new Set<string>();
  for (const item of scanned) {
    if (item.predicate) {
      continue;
    }
    for (const match of item.text.matchAll(EXPRESSION)) {
      const root = match[1];
      const owner = item.node ?? "";
      if (!VALID_ROOTS.has(root)) {
        const key = `bad:${owner}:${root}`;
        if (!seen.has(key)) {
          seen.add(key);
          findings.push(
            finding(
              "expression_root",
              `expression root \`${root}\` is not one of the fixed roots — it names a vendor, not what the interpreter, harness or workspace provider knows`,
              item.node,
            ),
          );
        }
      } else if (root === "previous" && !hasTrigger) {
        const key = `previous:${owner}`;
        if (!seen.has(key)) {
          seen.add(key);
          findings.push(
            finding(
              "expression_root",
              "expression root `previous` is legal only inside a trigger predicate; this document declares no trigger",
              item.node,
            ),
          );
        }
      }
    }
  }
};

const checkHeadFields = (
  document: LoopDocument,
  index: Map<string, Array<LoopDocument>>,
  scanned: ReadonlyArray<Scanned>,
  findings: Array<ValidationFinding>,
): void => {
  const nodes = document.nodes ?? {};
  const seen = new Set<string>();
  for (const item of scanned) {
    for (const match of item.text.matchAll(HEAD_READ)) {
      if (item.text[match.index + match[0].length] === ".") {
        continue;
      }
      const name = match[1];
      const field = match[2];
      const node = nodes[name];
      const loopRef = node === undefined ? undefined : node.loop;
      if (loopRef === undefined) {
        continue;
      }
      const resolution = resolveRef(index, loopRef);
      const head = resolution === undefined ? undefined : resolution.document?.head;
      if (head === undefined || head.includes(field)) {
        continue;
      }
      const key = `${name}.${field}`;
      if (!seen.has(key)) {
        seen.add(key);
        findings.push(
          finding(
            "head_fields_declared",
            `\`nodes.${name}.outcome.${field}\` reads a field not declared in the head of \`${loopRef}\``,
            name,
          ),
        );
      }
    }
  }
};

const checkVersions = (
  document: LoopDocument,
  index: Map<string, Array<LoopDocument>>,
  findings: Array<ValidationFinding>,
): void => {
  const check = (id: NodeId, ref: string): void => {
    const resolution = resolveRef(index, ref);
    if (resolution === undefined || !resolution.nameKnown || resolution.document !== undefined) {
      return;
    }
    findings.push(finding("version_resolves", `reference \`${ref}\` does not resolve in the available loops`, id));
  };
  for (const [id, node] of Object.entries(document.nodes ?? {})) {
    if (node.loop !== undefined) {
      check(id, node.loop);
    }
    if (node.shadow !== undefined) {
      check(id, node.shadow);
    }
    const block = isRecord(node.for_each) ? node.for_each : undefined;
    const source = block === undefined ? undefined : block["source"];
    if (isRecord(source) && typeof source["loop"] === "string") {
      check(id, source["loop"]);
    }
  }
};

// --- the structured-outcomes rules (model v3.4) ----------------------------

/** The `schema` of a declared `outcome: { head, schema }`; `undefined` for `outcome: true` or prose. */
const declaredSchema = (outcome: unknown): unknown => isRecord(outcome) ? outcome["schema"] : undefined;

/** The schema a node's outcome carries: its own, else the one its `loop:` callee declares. */
const nodeSchema = (node: LoopNode, index: Map<string, Array<LoopDocument>>): unknown => {
  const own = declaredSchema(node.outcome);
  if (own !== undefined) {
    return own;
  }
  if (node.loop === undefined) {
    return undefined;
  }
  const resolution = resolveRef(index, node.loop);
  return resolution === undefined ? undefined : declaredSchema(resolution.document?.outcome);
};

interface SchemaSite {
  readonly label: string;
  readonly node: NodeId | undefined;
  readonly schema: unknown;
}

/** Every schema the document declares: its own outcome, its `schemas:` map, each node's outcome. */
const schemaSites = (document: LoopDocument): ReadonlyArray<SchemaSite> => {
  const sites: Array<SchemaSite> = [];
  const own = declaredSchema(document.outcome);
  if (own !== undefined) {
    sites.push({ label: "`outcome.schema`", node: undefined, schema: own });
  }
  for (const [name, schema] of Object.entries(document.schemas ?? {})) {
    sites.push({ label: `\`schemas.${name}\``, node: undefined, schema });
  }
  for (const [id, node] of Object.entries(document.nodes ?? {})) {
    const schema = declaredSchema(node.outcome);
    if (schema !== undefined) {
      sites.push({ label: `\`outcome.schema\` of \`${id}\``, node: id, schema });
    }
  }
  return sites;
};

/** `outcome_schema_valid` + `outcome_ref_resolves`: every declared schema is in the subset and resolves. */
const checkOutcomeSchemas = (
  document: LoopDocument,
  resolvable: ReadonlyArray<LoopDocument>,
  findings: Array<ValidationFinding>,
): void => {
  for (const site of schemaSites(document)) {
    for (const problem of checkSubset(site.schema)) {
      findings.push(finding("outcome_schema_valid", `${site.label}${problem.path}: ${problem.message}`, site.node));
    }
    for (const reference of collectRefs(site.schema)) {
      if (reference.ref.includes("://")) {
        continue;
      }
      const resolved = resolveRefIn(site.schema, document, reference.ref, resolvable);
      if (Either.isLeft(resolved)) {
        findings.push(
          finding("outcome_ref_resolves", `${site.label}${reference.path}: ${resolved.left.message}`, site.node),
        );
      }
    }
  }
};

type Routes = NonNullable<LoopNode["routes"]>;

const routeMap = (routes: LoopNode["routes"]): Routes => routes ?? {};

/** The node name one route target names — `"done"`, `"stay"`, or `{ to, delay }`. */
const targetName = (target: Routes[string]): string => typeof target === "string" ? target : target.to;

/**
 * Whether one `bounds:` entry actually DRAWS a budget: it names a `pool:`, or it
 * carries a numeric `max > 0` and no `key:` (a keyed bound counts per external
 * value, so it bounds nothing about a cycle).
 */
const drawsBudget = (
  bound: { readonly max?: unknown; readonly key?: unknown; readonly pool?: string | undefined },
): boolean => bound.pool !== undefined || (bound.key === undefined && typeof bound.max === "number" && bound.max > 0);

/** Every verdict/cause a node's DRAWING `bounds:` entries declare. */
const boundOns = (bounds: LoopNode["bounds"]): ReadonlySet<string> => {
  const out = new Set<string>();
  for (const entry of toArray(bounds)) {
    if (!drawsBudget(entry)) {
      continue;
    }
    for (const value of toArray(entry.on)) {
      out.add(value);
    }
  }
  return out;
};

/** Every node a node's `routes:` name — its fall-through consumers. */
const routeTargets = (routes: LoopNode["routes"]): ReadonlyArray<string> =>
  Object.values(routeMap(routes)).map(targetName);

/** One `routes:` edge — `from --verdict--> to`, with `stay` resolved to its own node. */
interface RouteEdge {
  readonly from: string;
  readonly verdict: string;
  readonly to: string;
  readonly bounded: boolean;
}

/**
 * Every `routes:` edge in the graph, each marked bounded or not. Only `routes:`
 * count — a `timeout:`, an `every:` beat and a watch preempt a node, they do not
 * re-enter it on a verdict. An edge is bounded when its source declares a
 * drawing `bounds:` entry for that verdict (or for `entries`, which draws on
 * every one), or when its target caps its own entries with `max_attempts`.
 */
const routeEdges = (nodes: Record<string, LoopNode>): ReadonlyArray<RouteEdge> => {
  const capped = new Set(
    Object.entries(nodes).filter(([, node]) => node.max_attempts !== undefined).map(([id]) => id),
  );
  const edges: Array<RouteEdge> = [];
  for (const [from, node] of Object.entries(nodes)) {
    const drawn = boundOns(node.bounds);
    for (const [verdict, target] of Object.entries(routeMap(node.routes))) {
      const named = targetName(target);
      const to = named === "stay" ? from : named;
      edges.push({
        from,
        verdict,
        to,
        bounded: drawn.has(verdict) || drawn.has("entries") || capped.has(to),
      });
    }
  }
  return edges;
};

/**
 * The first path from `from` back to `target` over UNBOUNDED edges only, or
 * `undefined` when none exists. Depth-first; `seen` keeps a branch from walking
 * an inner cycle forever.
 */
const unboundedPathBack = (
  from: string,
  target: string,
  edges: ReadonlyArray<RouteEdge>,
  seen: ReadonlySet<string>,
): ReadonlyArray<RouteEdge> | undefined => {
  for (const edge of edges) {
    if (edge.from !== from || edge.bounded) {
      continue;
    }
    if (edge.to === target) {
      return [edge];
    }
    if (seen.has(edge.to)) {
      continue;
    }
    const rest = unboundedPathBack(edge.to, target, edges, new Set([...seen, edge.to]));
    if (rest !== undefined) {
      return [edge, ...rest];
    }
  }
  return undefined;
};

/** Render a cycle as `define -(success)-> spec_check -(over_budget)-> repair -(again)-> define`. */
const renderCycle = (start: string, path: ReadonlyArray<RouteEdge>): string =>
  path.reduce((text, edge) => `${text} -(${edge.verdict})-> ${edge.to}`, start);

/** `outcome_invalid_routed`: a typed outcome is routed, and a retry of it is bounded. */
const checkOutcomeRouting = (
  document: LoopDocument,
  index: Map<string, Array<LoopDocument>>,
  findings: Array<ValidationFinding>,
): void => {
  const nodes = document.nodes ?? {};
  const edges = routeEdges(nodes);
  for (const [id, node] of Object.entries(nodes)) {
    if (isSinkNode(node) || nodeSchema(node, index) === undefined) {
      continue;
    }
    const routed = OUTCOME_INVALID in routeMap(node.routes)
      || routeTargets(node.routes).some((target) => OUTCOME_INVALID in routeMap(nodes[target]?.routes));
    if (!routed) {
      findings.push(
        finding(
          "outcome_invalid_routed",
          `\`${id}\` declares a typed outcome but neither it nor a fall-through consumer routes \`${OUTCOME_INVALID}\``,
          id,
        ),
      );
    }
    // A typed producer that can be re-entered over a cycle drawing NO budget
    // re-runs forever. The refusal that sends it round need not be the
    // producer's OWN verdict: definition/task looped `define -(success)->
    // spec_check -(over_budget)-> repair -(again)-> define`, with the only
    // `bounds:` entry sitting on a verdict that cycle never takes (OL-329).
    const cycle = unboundedPathBack(id, id, edges, new Set([id]));
    if (cycle !== undefined) {
      findings.push(
        finding(
          "outcome_invalid_routed",
          `a route back into \`${id}\` sits under no \`bounds:\` entry that draws on it: ${renderCycle(id, cycle)}`,
          id,
        ),
      );
    }
  }
};

/** A whole-value bind of a producer's output: `${nodes.<p>.output}` or a sub-path of it. */
const OUTPUT_BIND = /^\$\{\s*nodes\.([A-Za-z_][A-Za-z0-9_]*)\.output((?:\.[A-Za-z_][A-Za-z0-9_]*)*)\s*\}$/;

/** The same read, unanchored — how a `for_each` names the collection it expands. */
const OUTPUT_READ_ANY = /\$\{\s*nodes\.([A-Za-z_][A-Za-z0-9_]*)\.output((?:\.[A-Za-z_][A-Za-z0-9_]*)*)\s*\}/;

/** A bind of one fan-out item: the whole value `${item}`, or a sub-path of it. */
const ITEM_BIND = /^\$\{\s*item((?:\.[A-Za-z_][A-Za-z0-9_]*)*)\s*\}$/;

type Lookup =
  | { readonly _tag: "None" }
  | { readonly _tag: "Schema"; readonly schema: unknown }
  | { readonly _tag: "Invalid"; readonly detail: string };

const NO_PRODUCER: Lookup = { _tag: "None" };

/** The schema a `${nodes.<p>.output…}` read produces, walked down its sub-path. */
const producerOf = (
  match: RegExpExecArray,
  nodes: Record<string, LoopNode>,
  index: Map<string, Array<LoopDocument>>,
  refs: SchemaResolver,
): Lookup => {
  const producer = nodes[match[1]];
  const schema = producer === undefined ? undefined : nodeSchema(producer, index);
  if (schema === undefined) {
    return NO_PRODUCER;
  }
  const segments = match[2] === "" ? [] : match[2].slice(1).split(".");
  const walked = propertyAt(schema, segments, refs);
  return Either.isLeft(walked)
    ? { _tag: "Invalid", detail: `\`${match[0]}\` ${walked.left.message}` }
    : { _tag: "Schema", schema: walked.right };
};

/** The collection expression a node's `for_each` expands — the bare form or the block's `source`. */
const forEachSource = (forEach: LoopNode["for_each"]): string | undefined => {
  const source = typeof forEach === "string" ? forEach : forEach?.source;
  return typeof source === "string" ? source : undefined;
};

/**
 * The schema a `${item…}` bind produces: the `items` schema of the collection
 * the node's `for_each` expands, walked down the bind's sub-path.
 */
const itemProducer = (
  node: LoopNode,
  bind: RegExpExecArray,
  nodes: Record<string, LoopNode>,
  index: Map<string, Array<LoopDocument>>,
  refs: SchemaResolver,
): Lookup => {
  const source = forEachSource(node.for_each);
  const match = source === undefined ? null : OUTPUT_READ_ANY.exec(source);
  if (match === null) {
    return NO_PRODUCER;
  }
  const collection = producerOf(match, nodes, index, refs);
  if (collection._tag !== "Schema") {
    return collection;
  }
  const items = itemsOf(collection.schema, refs);
  if (Option.isNone(items)) {
    return NO_PRODUCER;
  }
  const segments = bind[1] === "" ? [] : bind[1].slice(1).split(".");
  const walked = propertyAt(items.value, segments, refs);
  return Either.isLeft(walked)
    ? { _tag: "Invalid", detail: `\`${bind[0]}\` ${walked.left.message}` }
    : { _tag: "Schema", schema: walked.right };
};

/** `outcome_contract`: every input bound from a typed producer is assignable to it. */
const checkOutcomeContracts = (
  document: LoopDocument,
  index: Map<string, Array<LoopDocument>>,
  resolvable: ReadonlyArray<LoopDocument>,
  findings: Array<ValidationFinding>,
): void => {
  const nodes = document.nodes ?? {};
  for (const [id, node] of Object.entries(nodes)) {
    const callee = node.loop === undefined ? undefined : resolveRef(index, node.loop)?.document;
    if (callee === undefined || !isRecord(node.inputs)) {
      continue;
    }
    const declared = new Map<string, unknown>();
    for (const input of callee.inputs ?? []) {
      if (input.schema !== undefined) {
        declared.set(input.name, input.schema);
      }
    }
    const refs = schemaResolver([callee, document, ...resolvable]);
    for (const [name, binding] of Object.entries(node.inputs)) {
      const want = declared.get(name);
      if (want === undefined || typeof binding !== "string") {
        continue;
      }
      const bound = OUTPUT_BIND.exec(binding);
      const item = bound === null ? ITEM_BIND.exec(binding) : null;
      const lookup = bound !== null
        ? producerOf(bound, nodes, index, refs)
        : item !== null
        ? itemProducer(node, item, nodes, index, refs)
        : NO_PRODUCER;
      const site = `input \`${name}\` of \`${node.loop}\``;
      if (lookup._tag === "Invalid") {
        findings.push(finding("outcome_contract", `${site}: ${lookup.detail}`, id));
        continue;
      }
      if (lookup._tag === "Schema") {
        for (const problem of assignable(lookup.schema, want, refs)) {
          findings.push(finding("outcome_contract", `${site}${problem.path}: ${problem.message}`, id));
        }
      }
    }
  }
};

const buildIndex = (resolvable: ReadonlyArray<LoopDocument>): Map<string, Array<LoopDocument>> => {
  const index = new Map<string, Array<LoopDocument>>();
  for (const document of resolvable) {
    const group = index.get(document.loop) ?? [];
    group.push(document);
    index.set(document.loop, group);
  }
  return index;
};

/**
 * Validate a loop document at load. Findings accumulate — a document with three
 * problems returns a `Left` of three findings. `resolvable` is the set of other
 * loop documents available for `@version` and head-field resolution.
 */
/**
 * The two static rules the `resources:` seam adds.
 *
 * `resource_declared` — an `await:` whose event is a `<resource>:<event>`
 * resource event name may only name a resource the document's `resources:` map
 * declares. A legacy dotted event (`github.check_suite.completed`) is not a
 * resource event name and is not checked: it is delivered by the harness's own
 * channel, which needs no binding. Applies to a node's own `await:` and to an
 * `await:`-carrying `watch:` entry alike.
 *
 * `await_every_forbidden` — a node whose `await:` names a resource may not also
 * carry `every:`. The resource poller supplies the cadence; an `every:` beat on
 * a resource await would be a second, walk-driven poll of the same resource.
 *
 * (A third rule, `watch_when_on_await`, refused a `watch:` entry carrying both
 * `await:` and `when:`. It is gone: `watchSatisfied` now judges an awaiting
 * entry's `when` against the arrival, so the conjunction a document declares is
 * the conjunction it gets.)
 */
const checkResources = (document: LoopDocument, findings: Array<ValidationFinding>): void => {
  const declared = new Set<string>(Object.keys(document.resources ?? {}));
  const checkDeclared = (event: string, where: string, node?: NodeId): Option.Option<string> => {
    const resource = resourceOfEventName(event);
    if (Option.isSome(resource) && !declared.has(resource.value)) {
      findings.push(
        finding(
          "resource_declared",
          `${where} awaits \`${event}\`, but resource \`${resource.value}\` is not declared in \`resources:\``,
          node,
        ),
      );
    }
    return resource;
  };
  for (const [id, node] of Object.entries(document.nodes ?? {})) {
    if (node.await === undefined) {
      continue;
    }
    const resource = checkDeclared(node.await.event, `node \`${id}\``, id);
    if (Option.isSome(resource) && node.every !== undefined) {
      findings.push(
        finding(
          "await_every_forbidden",
          `node \`${id}\` awaits resource \`${resource.value}\` and also declares \`every:\` — the resource poller supplies the cadence`,
          id,
        ),
      );
    }
  }
  (document.watch ?? []).forEach((watch, index) => {
    const label = `watch entry \`${watch.name ?? index}\``;
    if (watch.await === undefined) {
      return;
    }
    checkDeclared(watch.await.event, label);
  });
};

export const validateDocument = (
  document: LoopDocument,
  resolvable: ReadonlyArray<LoopDocument> = [],
): Either.Either<ValidatedDocument, ReadonlyArray<ValidationFinding>> => {
  const findings: Array<ValidationFinding> = [];
  const index = buildIndex(resolvable);
  if (document.nodes !== undefined) {
    checkGraph(document, document.nodes, findings);
  }
  const scanned = scanDocument(document);
  checkExpressions(document, scanned, findings);
  checkHeadFields(document, index, scanned, findings);
  checkVersions(document, index, findings);
  checkOutcomeSchemas(document, resolvable, findings);
  checkOutcomeRouting(document, index, findings);
  checkOutcomeContracts(document, index, resolvable, findings);
  checkResources(document, findings);
  return findings.length === 0 ? Either.right({ document }) : Either.left(findings);
};

/** The `DocumentValidator` the interpreter takes through `makeInterpreter({ extensions })`. */
export const documentValidator = {
  validate: (
    document: LoopDocument,
  ): Either.Either<ValidatedDocument, ReadonlyArray<ValidationFinding>> => validateDocument(document),
};
